<template>
  <v-container
    fluid
    tag="section"
  >
    <v-card>
      <v-card-text>
        <v-row dense>
          <v-col
            cols="12"
            sm="2"
          >
            <v-btn
              color="primary"
              outlined
              @click="$refs.filter.open()"
            >
              <v-icon class="ml-3">
                mdi-filter-variant
              </v-icon>
              <span class="mx-3">Фильтры</span>
            </v-btn>
          </v-col>
          <v-col
            cols="12"
            sm="7"
            class="d-sm-flex justify-end"
          >
            <v-btn
              icon
              title="Настройка столбцов"
              @click="openSkudHeaders()"
            >
              <v-icon>mdi-view-column</v-icon>
            </v-btn>
            <v-btn
              icon
              title="Обновить"
              @click="load()"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-alert
          v-if="!loading && items.length === limit"
          type="warning"
          outlined
          dense
          class="mt-4 mb-0"
        >
          Возможно, показаны не все записи ({{ limit }}), сузьте фильтры
        </v-alert>
        <!-- ponytail: клиентская сортировка/пагинация только по загруженным N; keyset-курсоры аудита в API не документированы -->
        <v-data-table
          :headers="headers.filter(a => a.show || a.active)"
          :items="items"
          :loading="loading"
          :footer-props="datatable_footer"
          no-data-text="Нет данных для отображения"
        >
          <template v-slot:footer.page-text="{pageStart,pageStop,itemsLength}">
            {{ pageStart }}-{{ pageStop }} из {{ itemsLength }}
          </template>
          <template v-slot:item.action_text="{item}">
            <v-chip
              small
              :color="item.action_color"
              text-color="white"
            >
              {{ item.action_text }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{item}">
            <skud-actions-menu v-if="item.old_values || item.new_values">
              <v-list-item @click="openDiff(item)">
                <v-list-item-content>
                  Сравнить
                </v-list-item-content>
              </v-list-item>
            </skud-actions-menu>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <skud-filter-dialog
      ref="filter"
      :fields="filterFields"
      storage-key="skud.audit"
      @apply="load()"
    />

    <v-dialog
      v-model="dialog_headers.show"
      persistent
      max-width="700px"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">Настройка столбцов</span>
        </v-card-title>
        <v-card-text>
          <v-data-table
            dense
            :items-per-page="10"
            :items="headers.filter(a => !a.show)"
            :headers="[{value: 'text', text: 'Название столбца'}, {value: 'active_temp', text: 'Отображение'}]"
            :footer-props="datatable_footer"
            no-data-text="Нет данных для отображения"
          >
            <template v-slot:item.active_temp="{item}">
              <v-checkbox v-model="item.active_temp" />
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary darken-1"
            outlined
            rounded
            small
            class="ml-2 mb-2"
            @click="saveSkudHeaders('skud_audit')"
          >
            <span class="mx-4">Сохранить</span>
          </v-btn>
          <v-btn
            color="secondary darken-1"
            outlined
            rounded
            small
            class="ml-2 mb-2"
            @click="dialog_headers.show = false"
          >
            <span class="mx-4">Закрыть</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialog_diff.show"
      max-width="900px"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">Изменения</span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <div class="text-subtitle-1 mb-2">
                Было
              </div>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <pre
                class="diff-pre"
                v-html="dialog_diff.oldHtml"
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <div class="text-subtitle-1 mb-2">
                Стало
              </div>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <pre
                class="diff-pre"
                v-html="dialog_diff.newHtml"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="secondary darken-1"
            outlined
            rounded
            small
            class="ml-2 mb-2"
            @click="dialog_diff.show = false"
          >
            <span class="mx-4">Закрыть</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import SkudFilterDialog from '@/views/skud/components/SkudFilterDialog.vue'
import SkudActionsMenu from '@/views/skud/components/SkudActionsMenu.vue'
import { headersMixin } from '@/views/skud/shared/modules/headers-mixin'
import { SkudAuditService } from '@/views/skud/shared/services/skud-audit-service'
import { AuditAction, enumOptions } from '@/views/skud/shared/models/enums'

function esc (s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

// JSON-строка -> объект; не JSON — null.
function tryParse (raw) {
  if (!raw) return null
  try {
    const v = JSON.parse(raw)
    return v && typeof v === 'object' ? v : null
  } catch (e) {
    return null
  }
}

// Построчный рендер одной стороны diff'а; ключи, чьи значения отличаются от другой стороны, подсвечены.
function renderSide (raw, otherRaw) {
  if (!raw) return '<span style="color:grey">—</span>'
  const obj = tryParse(raw)
  if (!obj) return esc(raw)
  const other = tryParse(otherRaw)
  const lines = []
  for (const k of Object.keys(obj)) {
    const val = JSON.stringify(obj[k])
    let line = esc('"' + k + '": ' + val)
    if (other && Object.prototype.hasOwnProperty.call(other, k) && JSON.stringify(other[k]) !== val) {
      line = '<span style="background:#ffebee">' + line + '</span>'
    }
    lines.push(line)
  }
  return lines.join('\n')
}

export default {
  name: 'SkudAuditList',
  components: {
    SkudFilterDialog,
    SkudActionsMenu
  },
  mixins: [headersMixin],
  data () {
    return {
      svc: null,
      items: [],
      loading: false,
      limit: 100,
      datatable_footer: {
        itemsPerPageText: 'Показывать по',
        pageText: 'из',
        itemsPerPageOptions: [5, 10, 15, 30, 50]
      },
      headers: [
        { text: 'Действия', value: 'actions', show: true, sortable: false },
        { text: 'Дата/время', value: 'occurred_at', show: true },
        { text: 'Место', value: 'place_name', active: true, active_temp: true },
        { text: 'Актор', value: 'actor', active: true, active_temp: true },
        { text: 'Действие', value: 'action_text', active: true, active_temp: true },
        { text: 'Таблица', value: 'entity_table', active: true, active_temp: true },
        { text: 'ID сущности', value: 'entity_id', active: true, active_temp: true }
      ],
      filterFields: {
        period: {
          index: 1,
          name: 'Период',
          data: [
            { name: 'С', field: 'from', type: 'date' },
            { name: 'По', field: 'to', type: 'date' }
          ]
        },
        entity: {
          index: 2,
          name: 'Сущность',
          data: [
            { name: 'Таблица (entity_table)', field: 'entity_table', type: 'text' },
            { name: 'Действие', field: 'action', type: 'select', select: enumOptions(AuditAction) },
            { name: 'Актор', field: 'actor', type: 'text' },
            { name: 'ID сущности', field: 'entity_id', type: 'text' },
            { name: 'Лимит (1..500)', field: 'limit', type: 'number' }
          ]
        }
      },
      dialog_diff: { show: false, oldHtml: '', newHtml: '' }
    }
  },
  mounted () {
    this.svc = new SkudAuditService()
    this.setDefaultSkudHeaders(this.$store.state.user.skud_audit_headers)
    this.load()
  },
  methods: {
    async load () {
      const filters = this.$refs.filter.current()
      this.limit = Math.min(Math.max(Number(filters.limit) || 100, 1), 500)
      this.loading = true
      const res = await this.svc.getAudit(null, filters)
      this.loading = false
      if (this.svc.error) {
        this.$store.commit('snackbar', this.svc.error)
        return
      }
      this.items = res.items
    },
    openDiff (item) {
      this.dialog_diff.oldHtml = renderSide(item.old_values, item.new_values)
      this.dialog_diff.newHtml = renderSide(item.new_values, item.old_values)
      this.dialog_diff.show = true
    }
  }
}
</script>

<style scoped>
.diff-pre {
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 12px;
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 8px;
  min-height: 120px;
  max-height: 60vh;
  overflow-y: auto;
}
</style>
